// Provider-aware embed helper for tutor intro videos. Extracted from
// TutorOnboarding so the same YouTube/Vimeo detection is reused everywhere a
// video is shown (onboarding preview, tutor profile, catalog, admin) — #130.
//
// Returns an embeddable iframe URL for a recognized YouTube/Vimeo link, or null
// for anything else (the caller then falls back to an external link or hides
// the preview).
export function videoEmbedUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw.trim());
    if (url.hostname.includes("youtube.com")) {
      const id = url.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (url.hostname === "youtu.be") {
      const id = url.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (url.hostname.includes("vimeo.com")) {
      const id = url.pathname.split("/").filter(Boolean).pop();
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
  } catch {
    return null;
  }
  return null;
}

// Why a tutor's intro link is or isn't usable. Kept next to videoEmbedUrl so
// the rules that decide "we can embed this" and "we should accept this" can
// never drift apart.
//
// `shorts` is deliberately its own outcome rather than folded into
// `unsupported`: a Shorts link is proof the tutor filmed vertically, which is
// the single most common complaint about tutor intro videos, and it deserves
// an answer that says so instead of a generic "bad link".
export type VideoUrlProblem = "ok" | "shorts" | "unsupported" | "malformed" | "blank";

export function classifyVideoUrl(raw: string | null | undefined): VideoUrlProblem {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return "blank";

  // People copy links without the scheme far more often than they mistype the
  // host, so retry once with https:// before calling it malformed.
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return "malformed";
  }
  if (!url.hostname.includes(".")) return "malformed";

  const host = url.hostname.replace(/^www\./, "");
  const isYouTube = host === "youtube.com" || host === "m.youtube.com" || host === "youtu.be";

  if (isYouTube && url.pathname.startsWith("/shorts/")) return "shorts";
  if (videoEmbedUrl(url.toString())) return "ok";
  return "unsupported";
}
